import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  model,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Logger } from '@crt/sui';
import { NgbActiveModal, NgbAlert } from '@ng-bootstrap/ng-bootstrap';
import { CookieService } from 'ngx-cookie-service';
import { filter, fromEvent, interval, map, takeWhile } from 'rxjs';

import { UserState } from '@app/auth/user.state';
import { InactivityConfig } from '@app/common.model';
import { PlaceholderComponent } from '@app/homepage/placeholder/placeholder.component';
import { ModalFooterComponent } from '@app/shared/modal/modal-footer.component';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ModalFooterComponent, PlaceholderComponent, NgbAlert],
  selector: 'app-inactivity-warning-modal',
  templateUrl: './inactivity-warning-modal.component.html',
})
export class InactivityWarningModalComponent {
  private readonly cookieService = inject(CookieService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly modal = inject(NgbActiveModal);
  private readonly router = inject(Router);
  private readonly userState = inject(UserState);

  public readonly config = model<InactivityConfig>();

  /** Give the user 5 minutes to react */
  private readonly secondsToReact = signal<number>(300);

  /** Track if the cookie is expiring within 5 minutes */
  protected readonly isCookieExpiring = signal<boolean>(false);

  /** BroadcastChannel for cross-tab communication */
  private readonly broadcastChannel =
    typeof BroadcastChannel !== 'undefined'
      ? new BroadcastChannel('inactivity-modal-dismiss')
      : null;

  protected readonly countdown = computed(() => {
    const seconds = this.secondsToReact();
    const minutes = Math.floor(seconds / 60);
    const remainingSecs = seconds % 60;
    return `${minutes}:${remainingSecs.toString().padStart(2, '0')}`;
  });

  constructor() {
    this.checkTokenExpiration();

    interval(1000)
      .pipe(
        takeWhile(() => this.secondsToReact() > 0),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.secondsToReact.update((remaining) => remaining - 1);
      });

    effect(() => {
      if (this.secondsToReact() === 0) {
        this.modal.close(false);
        this.userState.logout$.next();
        this.router.navigateByUrl('/u/login');
      }
    });

    // Listen for cross-tab dismiss messages to close modal and reset countdown
    if (this.broadcastChannel) {
      fromEvent<MessageEvent>(this.broadcastChannel, 'message')
        .pipe(
          map((event) => event.data),
          filter((data): data is { type: 'dismiss-modal' } => data?.type === 'dismiss-modal'),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe(() => {
          // Close modal and reset countdown when dismissed in another tab
          this.secondsToReact.set(300);
          this.modal.close(true);
        });
    }
  }

  protected onConfirm(reLogin?: boolean): void {
    this.modal.close(true);

    if (reLogin) {
      this.userState.logout$.next();
      this.router.navigateByUrl('/u/login');
    }
  }

  private checkTokenExpiration(): void {
    const token = this.cookieService.get('XSRF-TOKEN');
    if (!token) {
      return;
    }

    try {
      const base64Url = token.split('.')[1];
      if (!base64Url) {
        return;
      }

      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const payload = JSON.parse(window.atob(base64));

      if (payload.exp) {
        const timeUntilExpiration = payload.exp * 1000 - Date.now();
        const isExpiring = timeUntilExpiration > 0 && timeUntilExpiration < 300000;
        this.isCookieExpiring.set(isExpiring);
      }
    } catch (error) {
      Logger.error('Error parsing XSRF-TOKEN:', { data: error });
    }
  }
}
